'use client';
import { useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Button } from '@/ui-components/atoms/Button/Button';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import { ensureDeviceGrant } from '@/services/microservices/deviceGrant';
import { getClientSdk } from '@/ui-components/organisms/AccountSnapshot/clientSdk';
import { getAuthenticatedIdentityId } from '@/ui-components/organisms/AccountSnapshot/snapshotSignInFlowServices';
import { FlexibleFormBase, FlexibleFormVariant } from './FlexibleFormBase';
import { parseFormSubmitResponse, SMS_OPT_IN_FIELD_NAME } from './flexibleFormShared';
import { createFlexibleFormCustomSubmit } from './flexibleFormCustomSubmit';
import { createFlexibleFormLayoutConfig } from './flexibleFormLayoutConfig';
import { createFlexibleFormTypeVariantOverrides } from './flexibleFormTypeVariantOverrides';
import styles from './flexibleForm.module.scss';

const { successHeadlineStyles, successErrorWrapper, subheadlineStyles, ctaStyles, ctaCaption } = styles;

interface ClientFlexibleFormProps {
    asset: SiteFlexibleForm;
    nestedForm?: boolean;
    toggleForm?: boolean;
    forceRflzSubmit?: boolean;
    focusFirstFieldRef?: { current: (() => void) | null };
    onSearchByLicensePlate?: () => void;
    externalFormErrorMessage?: string | React.JSX.Element;
}

export function FlexibleFormBuilder({
    asset,
    nestedForm = false,
    toggleForm = false,
    forceRflzSubmit = false,
    focusFirstFieldRef,
    onSearchByLicensePlate,
    externalFormErrorMessage,
}: ClientFlexibleFormProps) {
    const {
        fields: {
            type,
            success_headline,
            error_headline,
            success_subheadline,
            error_subheadline,
            error_cta,
            success_page_redirect,
        },
    } = asset;
    const formType = type.toLowerCase();
    const router = useRouter();
    const searchParams = useSearchParams();
    const [deviceId, setDeviceId] = useState('');
    const [identityId, setIdentityId] = useState('');
    const [submitSucceeded, setSubmitSucceeded] = useState(false);
    const needsDeviceId = asset.fields.form_fields.some(
        (formField) => formField.fields.field_name === SMS_OPT_IN_FIELD_NAME,
    );
    const isSmsOptInForm = formType === 'web_standalone_page';

    useEffect(() => {
        if (!needsDeviceId) {
            return;
        }

        let cancelled = false;
        ensureDeviceGrant().then((grant) => {
            if (!cancelled && grant) {
                setDeviceId(grant.deviceId);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [needsDeviceId]);

    useEffect(() => {
        if (!isSmsOptInForm) {
            return;
        }

        let cancelled = false;
        getClientSdk()
            .then((clientSdk) => (clientSdk ? getAuthenticatedIdentityId(clientSdk) : undefined))
            .then((resolvedIdentityId) => {
                if (!cancelled && resolvedIdentityId) {
                    setIdentityId(resolvedIdentityId);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isSmsOptInForm]);

    useEffect(() => {
        if (submitSucceeded && success_page_redirect) {
            router.push(success_page_redirect.fields.href);
        }
    }, [submitSucceeded, success_page_redirect, router]);

    const customSubmit = createFlexibleFormCustomSubmit({ asset, forceRflzSubmit });
    const layoutConfig = createFlexibleFormLayoutConfig({ asset, nestedForm, toggleForm });
    const typeVariantOverrides = createFlexibleFormTypeVariantOverrides({ asset, searchParams, identityId });
    const extraHiddenFields: HiddenFormField[] = [
        ...(typeVariantOverrides.extraHiddenFields ?? []),
        ...(needsDeviceId ? [{ name: 'deviceId', value: deviceId }] : []),
    ];

    const flexibleFormBuilderVariant: FlexibleFormVariant = {
        formType,
        styles: layoutConfig.styles,
        analytics: {
            successEvent: `${formType}-success`,
            failEvent: `${formType}-fail`,
        },
        sourceName: formType,
        trackSubmitAttempted: true,
        submitButtonFitting: layoutConfig.submitButtonFitting,
        getStateSelectHeader: () => ({
            title: 'Select a state',
            body: null,
        }),
        getDropdownSelectHeader: () => ({
            title: 'Select',
            body: null,
        }),
        customSubmit,
        onSuccessResponse: async (response, { validations, setValidations, setSubmitState }) => {
            const json = await response.json();
            const parsedResponse = parseFormSubmitResponse(json, validations);

            if (parsedResponse.submitState) {
                setSubmitState(parsedResponse.submitState);
            }

            if (Object.keys(parsedResponse.serverValidations).length > 0) {
                setValidations({ ...validations, ...parsedResponse.serverValidations });
            }

            if (parsedResponse.submitState === 'success') {
                setSubmitSucceeded(true);
            }
        },
        renderContent: layoutConfig.renderContent,
        renderSubmitMessage: ({ submitState }) => {
            const success = submitState === 'success';
            const failed = submitState === 'failed';
            const errorCTA = error_cta?.fields;

            return success || failed ? (
                <div className={successErrorWrapper}>
                    {success && (
                        <ImageWrapper
                            src="https://www.siriusxm.com/content/dam/sxm-com/lottie/stella/Stella-Wag-Animation.gif"
                            alt="Stella Alert"
                            optimizedWidth={100}
                            optimizedHeight={100}
                            loadingStrategy="eager"
                            skipOptimization={true}
                        />
                    )}
                    <div data-cy={`${formType}-form-submit-success-message`} className={successHeadlineStyles}>
                        <Text tag="h5" variant="h5">
                            {success && success_headline}
                            {failed && error_headline}
                        </Text>
                    </div>
                    <Text tag="p" className={subheadlineStyles}>
                        {success && <RenderRTE contentfulDoc={success_subheadline} />}
                        {failed && <RenderRTE contentfulDoc={error_subheadline} />}
                    </Text>
                    {failed && errorCTA && (
                        <>
                            <Button
                                type="button"
                                className={ctaStyles}
                                href={errorCTA.href}
                                linkTarget={errorCTA.link_target}
                                ariaLabel={errorCTA.name}
                                variant={errorCTA.theme}
                                size={errorCTA.size.toLowerCase()}
                                fitting="hug-contents"
                                dataCy="sweeps-form-error-cta"
                            >
                                {errorCTA.link_text}
                            </Button>
                            {asset.fields.cta_caption && (
                                <RenderRTE contentfulDoc={asset.fields.cta_caption} className={ctaCaption} />
                            )}
                        </>
                    )}
                </div>
            ) : null;
        },
        renderShell: ({ submitState, submitMessage, form }) => (
            <>
                <div aria-live="polite">{submitMessage}</div>
                {submitState === 'untouched' || submitState === 'duplicate' ? form : undefined}
            </>
        ),
        ...typeVariantOverrides,
        extraHiddenFields,
    };

    return (
        <FlexibleFormBase
            asset={asset}
            nestedForm={nestedForm}
            variant={flexibleFormBuilderVariant}
            focusFirstFieldRef={focusFirstFieldRef}
            onSearchByLicensePlate={onSearchByLicensePlate}
            externalFormErrorMessage={externalFormErrorMessage}
        />
    );
}
